import React, { useEffect, useId, useState } from 'react';
import { Box, ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import appIcon from 'src/assets/images/logo/app-icon.webp';
import qrLogo from 'src/assets/images/logo/qr-logo.png';

// Triangle Google Play aux couleurs officielles (bleu, vert, jaune, rouge).
// `tile` le pose sur un carré blanc arrondi, comme l'icône de l'application.
// Ids de dégradé uniques par instance : un id partagé avec une copie masquée
// (display:none) ferait disparaître le dégradé des autres copies.
const GooglePlayLogo = ({ size = 20, tile = false }) => {
  const id = useId().replace(/:/g, '');
  const triangle = (
    <>
      <defs>
        <linearGradient id={`gpBlue${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00C6FF" />
          <stop offset="1" stopColor="#1A73E8" />
        </linearGradient>
      </defs>
      {/* Triangle (4,2)-(4,22)-(21,12) découpé en 4 zones qui se rejoignent en (12.4,12). */}
      <g strokeWidth="1" strokeLinejoin="round">
        <path fill={`url(#gpBlue${id})`} stroke={`url(#gpBlue${id})`} d="M4 2 12.4 12 4 22z" />
        <path fill="#1BC760" stroke="#1BC760" d="M4 2 16.2 9.2 12.4 12z" />
        <path fill="#FFC107" stroke="#FFC107" d="M16.2 9.2 21 12 16.2 14.8 12.4 12z" />
        <path fill="#F4364C" stroke="#F4364C" d="M4 22 12.4 12 16.2 14.8z" />
      </g>
    </>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {tile ? (
        <>
          <rect width="24" height="24" rx="5.4" fill="#fff" stroke="#E3E5E8" strokeWidth="0.6" />
          <g transform="translate(3.3 3.4) scale(0.72)">{triangle}</g>
        </>
      ) : triangle}
    </svg>
  );
};

// Icône App Store : « A » formé de trois bâtonnets blancs sur un carré bleu arrondi.
const AppStoreLogo = ({ size = 38 }) => {
  const gradientId = `appStoreGradient${useId().replace(/:/g, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#14D0FF" />
          <stop offset="1" stopColor="#0A6CF0" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="5.4" fill={`url(#${gradientId})`} />
      <g stroke="#fff" strokeWidth="2.2" strokeLinecap="round" fill="none">
        <path d="M14.3 5.4 8.8 14.9" />
        <path d="M7.5 17.1 6.8 18.3" />
        <path d="M9.7 5.4 10.8 7.3" />
        <path d="M12.6 10.4 17.2 18.3" />
        <path d="M5.6 14.9h12.8" />
      </g>
    </svg>
  );
};

// Icône carrée pour les petits boutons du bandeau sur mobile.
const GooglePlayTile = ({ size = 38 }) => <GooglePlayLogo size={size} tile />;

// Mêmes liens que la landing page (AppDownloadBanner / ExternalLinkModal).
const STORES = {
  android: {
    label: 'Google Play',
    caption: 'GET IT ON',
    url: 'https://play.google.com/store/apps/details?id=com.CliClaim.Client',
    Logo: GooglePlayLogo,
    Tile: GooglePlayTile
  },
  ios: {
    label: 'App Store',
    caption: 'Download on the',
    url: 'https://apps.apple.com/tn/app/cliclaim-client/id6749679124?l=fr-FR',
    Logo: AppStoreLogo,
    Tile: AppStoreLogo
  }
};

// Badge noir au style des badges officiels, mais avec l'icône de chaque store
// (le badge officiel d'Apple impose la pomme, l'équipe veut l'icône App Store).
const StoreBadge = ({ store, height = 38 }) => {
  const { Logo, caption, label } = store;
  return (
    <Box sx={{
      height, display: 'flex', alignItems: 'center', gap: 1, px: 1.25,
      bgcolor: '#000', color: '#fff', borderRadius: 1.5, border: '1px solid #A6A6A6'
    }}>
      <Logo size={Math.round(height * 0.6)} />
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1, textAlign: 'start' }}>
        <Box component="span" sx={{ fontSize: height * 0.22, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>{caption}</Box>
        <Box component="span" sx={{ fontSize: height * 0.42, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</Box>
      </Box>
    </Box>
  );
};

// Hauteur réservée en bas de page pour que le bandeau ne masque pas les derniers boutons.
const BANNER_SPACE = { xs: 80, sm: 96 };

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ se présente comme un Mac : on le distingue par l'écran tactile.
  return /android|iPad|iPhone|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
};

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true);

/* Bandeau flottant en bas de l'écran invitant à installer l'application mobile.
   Sur téléphone, chaque badge ouvre directement son store ; sur ordinateur,
   il ouvre le QR code de ce store. Comme sur la landing page, la fermeture n'est
   pas mémorisée : le bandeau revient au rechargement et après une reconnexion. */
export default function AppDownloadBanner() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [qrStore, setQrStore] = useState(null);
  const mobile = isMobileDevice();

  useEffect(() => {
    if (!isStandalone()) setVisible(true);
  }, []);

  const dismiss = () => setVisible(false);

  if (!visible) return null;

  const store = qrStore ? STORES[qrStore] : null;

  return (
    <>
      <Box sx={{ height: BANNER_SPACE }} />

      {/* Conteneur pleine largeur transparent : seule la carte centrée capte les clics. */}
      <Box
        sx={{
          position: 'fixed', bottom: 0, insetInlineEnd: 0, insetInlineStart: 0, zIndex: 1200,
          [theme.breakpoints.up('lg')]: { insetInlineStart: theme.sidebar.width },
          display: 'flex', justifyContent: 'center', px: 1, pointerEvents: 'none'
        }}
      >
        <Box
          role="region"
          aria-label={t('appBanner.title')}
          sx={{
            pointerEvents: 'auto', width: '100%', maxWidth: 640,
            display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 },
            px: { xs: 1, sm: 1.5 }, py: 1, borderRadius: 3,
            bgcolor: '#fff', color: '#111820',
            boxShadow: '0 8px 28px rgba(0,0,0,0.45)'
          }}
        >
          <IconButton onClick={dismiss} aria-label={t('close')} size="small" sx={{ color: '#4A5560', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box component="img" src={appIcon} alt="" sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, borderRadius: 1.5, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: 13, sm: 15 }, color: '#111820', lineHeight: 1.2 }}>CliClaim</Typography>
            <Typography sx={{
              display: '-webkit-box', fontSize: { xs: 11, sm: 12 }, color: '#4A5560', lineHeight: 1.3,
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
            }}>
              {t('appBanner.subtitle')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
            {Object.entries(STORES).map(([key, s]) => (
              <ButtonBase
                key={key}
                aria-label={s.label}
                {...(mobile
                  ? { component: 'a', href: s.url, target: '_blank', rel: 'noopener noreferrer' }
                  : { onClick: () => setQrStore(key) })}
                title={s.label}
                sx={{
                  borderRadius: '22%', transition: 'transform 120ms ease',
                  '&:hover': { transform: 'translateY(-1px)' }
                }}
              >
                {/* Icône carrée du store, comme sur l'icône de l'application (mobile et ordinateur). */}
                <Box sx={{ display: 'flex', width: { xs: 38, sm: 44 }, height: { xs: 38, sm: 44 } }}>
                  <s.Tile size="100%" />
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog open={Boolean(store)} onClose={() => setQrStore(null)} maxWidth="xs"
        PaperProps={{ sx: { bgcolor: '#1C1F22', color: '#F2F4F5', borderRadius: 4, minWidth: 300 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: '#F2F4F5', pt: 3, px: 6 }}>
          {t('appBanner.qrTitle')}
          <IconButton onClick={() => setQrStore(null)} aria-label={t('close')}
            sx={{ position: 'absolute', top: 8, insetInlineEnd: 8, color: '#F2F4F5' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {store && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 3 }}>
            <QRCodeCanvas
              value={store.url} size={180} fgColor="#ffffff" bgColor="transparent" level="H"
              imageSettings={{ src: qrLogo, height: 34, width: 34, excavate: true }}
            />
            <Box component="a" href={store.url} target="_blank" rel="noopener noreferrer"
              sx={{ textDecoration: 'none', color: 'inherit', borderRadius: 1.5 }}
            >
              <StoreBadge store={store} height={44} />
            </Box>
            <Typography sx={{ textAlign: 'center', color: '#A7AFB6', fontSize: 13 }}>
              {t('appBanner.qrHint')}
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
